import { useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

const DATE_PRESETS = [
  { label: "7d",  days: 7  },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
] as const;

interface ScopeToggleProps {
  availableMetricScopes: ("all" | "bucket" | "goal")[];
  metricScope: "all" | "bucket" | "goal";
  projectScope: "project" | "team";
  onMetricScopeChange: (scope: "all" | "bucket" | "goal") => void;
  onProjectScopeChange: (scope: "project" | "team") => void;
  /** Single bucket label — used by production code. */
  objectiveBucket: string | null;
  /** Single goal label — used by production code. */
  optimizationGoalLabel: string | null;
  /**
   * Optional multi-bucket list. When provided, renders one pill per entry
   * instead of the single objectiveBucket pill. Tracks selection internally.
   */
  objectiveBuckets?: string[];
  /**
   * Optional multi-goal list. When provided, renders one pill per entry
   * instead of the single optimizationGoalLabel pill. Tracks selection internally.
   */
  optimizationGoals?: string[];
  sampleSize: number;
  warnings: string[];
  startDate?: string;
  endDate?: string;
  onDateRangeChange: (range: { startDate?: string; endDate?: string }) => void;
  peerMediaType?: "video" | "image";
  onPeerMediaTypeChange: (type: "video" | "image" | undefined) => void;
  className?: string;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en", { month: "short", day: "numeric" });
}

/** Scope selector: Funnel/Goal (metric scope) + Project scope + Peer filters (date + media type). */
export function ScopeToggle({
  availableMetricScopes,
  metricScope,
  projectScope,
  onMetricScopeChange,
  onProjectScopeChange,
  objectiveBucket,
  optimizationGoalLabel,
  objectiveBuckets,
  optimizationGoals,
  sampleSize,
  warnings,
  startDate,
  endDate,
  onDateRangeChange,
  peerMediaType,
  onPeerMediaTypeChange,
  className,
}: ScopeToggleProps) {
  const hasBucket = availableMetricScopes.includes("bucket");
  const hasGoal = availableMetricScopes.includes("goal");
  const showLevel2 = hasGoal && metricScope !== "all";

  // Multi-option support: resolve effective bucket/goal lists
  const bucketOptions = objectiveBuckets ?? (objectiveBucket ? [objectiveBucket] : []);
  const goalOptions = optimizationGoals ?? (optimizationGoalLabel ? [optimizationGoalLabel] : []);

  // Internally track which specific bucket/goal is selected when multiple options exist
  const [selectedBucket, setSelectedBucket] = useState<string | null>(bucketOptions[0] ?? null);
  const [selectedGoal, setSelectedGoal] = useState<string | null>(goalOptions[0] ?? null);

  // Determine which date preset is currently active (if any)
  const activeDatePreset = DATE_PRESETS.find((p) => {
    if (!startDate || !endDate) return false;
    const today = new Date();
    const expectedStart = new Date(today);
    expectedStart.setDate(today.getDate() - p.days);
    return startDate === expectedStart.toISOString().slice(0, 10);
  });

  // Custom is active when a date is set but it doesn't match any preset
  const isCustomActive = !!(startDate && !activeDatePreset);

  // Show custom inputs if: user clicked Custom, OR a custom range is already set
  const [showCustom, setShowCustom] = useState<boolean>(isCustomActive);

  // Accordion state for peer filters — auto-expand if filters already active on mount
  const peerTriggerActive = !!(startDate || endDate || peerMediaType);
  const [peersExpanded, setPeersExpanded] = useState(peerTriggerActive);

  // Human-readable label for a custom date range
  const customRangeLabel =
    startDate && endDate
      ? `${fmtDate(startDate)} → ${fmtDate(endDate)}`
      : "Custom";

  const clearAllPeerFilters = () => {
    onDateRangeChange({});
    onPeerMediaTypeChange(undefined);
    setShowCustom(false);
  };

  const handleDatePreset = (days: number) => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - days);
    onDateRangeChange({
      startDate: start.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
    });
    setShowCustom(false);
  };

  const handleCustomClick = () => {
    setShowCustom(true);
    // Pre-populate from active preset so user can refine — don't wipe existing range
    if (!startDate && activeDatePreset) {
      const today = new Date();
      const start = new Date(today);
      start.setDate(today.getDate() - activeDatePreset.days);
      onDateRangeChange({
        startDate: start.toISOString().slice(0, 10),
        endDate: today.toISOString().slice(0, 10),
      });
    }
  };

  const handleLevel1 = (value: "all" | "bucket") => {
    if (value === "all") {
      onMetricScopeChange("all");
    } else {
      if (metricScope === "all") {
        onMetricScopeChange("bucket");
      }
    }
  };

  const projectScopeSegments: { value: "project" | "team"; label: string }[] = [
    { value: "project", label: "This Project" },
    { value: "team",    label: "All Projects" },
  ];

  const pillBase = "px-3 py-1.5 text-xs font-medium rounded-md transition-colors";
  const pillActive = "bg-surface shadow-sm text-gray-900";
  const pillInactive = "text-gray-600 hover:text-gray-800";

  return (
    <div className={cn("space-y-0", className)}>
      {/* ── Metric scope row: Funnel / Goal + Project scope ────── */}
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="space-y-1.5">
          {/* Level 1 — Funnel Step */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider w-16 shrink-0">
              Funnel
            </span>
            <div className="inline-flex flex-wrap rounded-lg bg-gray-100 p-0.5">
              <button
                onClick={() => handleLevel1("all")}
                className={cn(pillBase, metricScope === "all" ? pillActive : pillInactive)}
              >
                All
              </button>
              {hasBucket && bucketOptions.map((bucket) => (
                <button
                  key={bucket}
                  onClick={() => { setSelectedBucket(bucket); handleLevel1("bucket"); }}
                  className={cn(
                    pillBase,
                    metricScope !== "all" && selectedBucket === bucket ? pillActive : pillInactive
                  )}
                >
                  {bucket}
                </button>
              ))}
            </div>
          </div>

          {/* Level 2 — Optimization Goal: left border line shows parent-child relationship */}
          {showLevel2 && (
            <div className="flex items-center gap-2 ml-[4.5rem] border-l-2 border-gray-200 pl-3">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider w-14 shrink-0">
                Goal
              </span>
              <div className="inline-flex flex-wrap rounded-lg bg-gray-100 p-0.5">
                <button
                  onClick={() => onMetricScopeChange("bucket")}
                  className={cn(pillBase, metricScope === "bucket" ? pillActive : pillInactive)}
                >
                  All {selectedBucket || objectiveBucket || "Bucket"}
                </button>
                {goalOptions.map((goal) => (
                  <button
                    key={goal}
                    onClick={() => { setSelectedGoal(goal); onMetricScopeChange("goal"); }}
                    className={cn(
                      pillBase,
                      metricScope === "goal" && selectedGoal === goal ? pillActive : pillInactive
                    )}
                  >
                    {goal}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Project scope + sample size with tooltip */}
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
            {projectScopeSegments.map((segment) => (
              <button
                key={segment.value}
                onClick={() => onProjectScopeChange(segment.value)}
                className={cn(
                  "px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors",
                  projectScope === segment.value ? pillActive : pillInactive
                )}
              >
                {segment.label}
              </button>
            ))}
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-gray-500 tabular-nums cursor-help underline decoration-dotted decoration-gray-300">
                  n={sampleSize}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {sampleSize} creatives match these filter criteria and are used for percentile calculations
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* ── Peer filter accordion ─────────────────────────────── */}
      <div className="mt-2.5 pt-2.5 border-t border-gray-100">
        {/* Trigger row: expand button + info tooltip + clear button (siblings, not nested) */}
        <div className="flex items-center gap-1">
          {/* Expand/collapse button — two visual states: ghost (inactive) vs blue badge (active) */}
          <button
            onClick={() => setPeersExpanded((v) => !v)}
            className={cn(
              "inline-flex items-center gap-1.5 text-[11px] rounded-md px-2 py-1 transition-colors",
              peerTriggerActive
                ? "bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100"
                : "text-gray-500 hover:text-gray-700 border border-transparent hover:border-gray-200"
            )}
          >
            {/* Blue dot when active */}
            {peerTriggerActive && (
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
            )}
            {/* Filter icon when inactive */}
            {!peerTriggerActive && (
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M7 8h10M11 12h2M11 16h2" />
              </svg>
            )}

            <span>Compare peers</span>

            {/* Active filter summary chips */}
            {activeDatePreset && <span>· {activeDatePreset.label}</span>}
            {isCustomActive && startDate && endDate && <span>· {customRangeLabel}</span>}
            {peerMediaType && (
              <span>· {peerMediaType === "image" ? "Images" : "Videos"}</span>
            )}

            {/* Chevron */}
            <svg
              className={cn("w-3 h-3 transition-transform", peersExpanded && "rotate-180")}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Info tooltip — sibling, never nested inside the button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="10" />
                    <path strokeLinecap="round" d="M12 16v-4M12 8h.01" />
                  </svg>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Filter which creatives are used as the comparison pool for percentile rankings
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Clear-all — sibling button, shown only when filters are active */}
          {peerTriggerActive && (
            <button
              onClick={clearAllPeerFilters}
              className="text-[11px] text-blue-400 hover:text-blue-700 px-1 transition-colors"
              aria-label="Clear all peer filters"
            >
              ✕
            </button>
          )}
        </div>

        {/* Expandable peer filter controls */}
        {peersExpanded && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Date group */}
              <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                {/* All Time */}
                <button
                  onClick={() => { onDateRangeChange({}); setShowCustom(false); }}
                  className={cn(pillBase, !startDate && !endDate && !showCustom ? pillActive : pillInactive)}
                >
                  All Time
                </button>
                {/* Presets: 7d / 30d / 90d */}
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.days}
                    onClick={() => handleDatePreset(preset.days)}
                    className={cn(
                      pillBase,
                      activeDatePreset?.days === preset.days ? pillActive : pillInactive
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
                {/* Custom — pre-populates from active preset, never destructive */}
                <button
                  onClick={handleCustomClick}
                  className={cn(
                    pillBase,
                    (showCustom || isCustomActive) ? pillActive : pillInactive
                  )}
                >
                  {isCustomActive && startDate && endDate ? customRangeLabel : "Custom"}
                </button>
              </div>

              {/* Divider */}
              <div className="w-px h-4 bg-gray-200 shrink-0" />

              {/* Media type group */}
              <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                <button
                  onClick={() => onPeerMediaTypeChange(undefined)}
                  className={cn(pillBase, !peerMediaType ? pillActive : pillInactive)}
                >
                  All
                </button>
                <button
                  onClick={() => onPeerMediaTypeChange("image")}
                  className={cn(pillBase, peerMediaType === "image" ? pillActive : pillInactive)}
                >
                  Images
                </button>
                <button
                  onClick={() => onPeerMediaTypeChange("video")}
                  className={cn(pillBase, peerMediaType === "video" ? pillActive : pillInactive)}
                >
                  Videos
                </button>
              </div>
            </div>

            {/* Custom date inputs — styled container, shown when Custom is active */}
            {(showCustom || isCustomActive) && (
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider shrink-0">
                  From
                </span>
                <input
                  type="date"
                  value={startDate || ""}
                  max={endDate || undefined}
                  onChange={(e) =>
                    onDateRangeChange({ startDate: e.target.value || undefined, endDate })
                  }
                  className="text-xs text-gray-700 bg-surface border border-gray-200 rounded-md px-2 py-1
                             focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-gray-300">→</span>
                <input
                  type="date"
                  value={endDate || ""}
                  min={startDate || undefined}
                  onChange={(e) =>
                    onDateRangeChange({ startDate, endDate: e.target.value || undefined })
                  }
                  className="text-xs text-gray-700 bg-surface border border-gray-200 rounded-md px-2 py-1
                             focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                {/* Clear custom range */}
                <button
                  onClick={() => { onDateRangeChange({}); setShowCustom(false); }}
                  className="text-[10px] text-gray-400 hover:text-gray-600 ml-1"
                  aria-label="Clear date range"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="mt-2 text-xs text-amber-600 bg-amber-50 rounded px-3 py-1.5">
          {warnings[0]}
        </div>
      )}
    </div>
  );
}
